import {inject, Injectable} from "@angular/core";
import { LogEvent } from "@core/enums/log-event";
import { LoggerService } from "@core/services/logger.service";

@Injectable({
  providedIn: 'root',
})
export class AudioPlayerService {
  private logger = inject(LoggerService);

  private _audios: { [key: string]: HTMLAudioElement } = {};
  private _playPromises: { [key: string]: Promise<void> | null } = {};
  private _playStarted: { [key: string]: boolean } = {};

  public async play(audioName: string, { loop = false, volume }: { loop: boolean; volume?: number }): Promise<void> {
    if (!this._audios[audioName]) {
      this._audios[audioName] = new Audio(`assets/audio/${audioName}`);
      this._audios[audioName].loop = loop;
    }

    if (volume !== undefined && Number.isFinite(Number(volume))) {
      this._audios[audioName].volume = this._clampVolume(volume);
    }

    try {
      const playPromise = this._audios[audioName].play();
      this._playPromises[audioName] = playPromise;
      this._playStarted[audioName] = false;
            await playPromise;
      this._playStarted[audioName] = true;
    } catch (error: any) {
      this._playStarted[audioName] = true;
      
      if (error.name !== 'AbortError') {
        this.logger.error(LogEvent.LIVECHAT_AUDIO_PLAYER_PLAY_ERROR, error as Error);
      }
    }
  }

  public async stop(audioName: string): Promise<void> {
    const startTime = Date.now();
    const maxWaitTime = 5000;
    
    while (!this._playStarted[audioName] && Date.now() - startTime < maxWaitTime) {
      await new Promise(resolve => setTimeout(resolve, 10));
    }
    
    if (this._audios[audioName]) {
      this._audios[audioName].pause();
      this._audios[audioName].currentTime = 0;
    }
    
    this._playPromises[audioName] = null;
    this._playStarted[audioName] = false;
  }

  public setVolume(audioName: string, volume: number): void {
    if (this._audios[audioName] && Number.isFinite(Number(volume))) {
      this._audios[audioName].volume = this._clampVolume(volume);
    }
  }

  private _clampVolume(volume: number): number {
    return Math.min(1, Math.max(0, Number(volume)));
  }

}
